import React, { useRef, useState } from 'react';
import { I18n, translate } from '@fiverr-private/i18n-react';
import { useObserver } from '@fiverr-private/orca';
import { Container, Stack } from '@fiverr-private/layout_components';
import { atoms } from '@fiverr-private/theme';
import { Heading, Text } from '@fiverr-private/typography';
import { AgencyService } from '../../../types';
import { bigQuery, EVENT_TYPE } from '../../../monitoring/bigQuery';
import { mixpanel } from '../../../monitoring/mixpanel';
import { PreviewItemIcon } from '../PreviewItemIcon';
import { PreviewItemSubService } from '../PreviewItemSubService';

import { moreButtonClass } from './styles.ve.css';

interface PreviewItemProps {
    index: number;
    item: AgencyService;
    showMoreExist: boolean;
    onSeeMoreClick: () => void;
}

export const PreviewItem = ({ index, item, onSeeMoreClick, showMoreExist }: PreviewItemProps) => {
    const componentRef = useRef<HTMLElement>(null);
    const [viewed, setViewed] = useState<boolean>(false);

    const onObserve = () => {
        if (!viewed) {
            bigQuery.impressionEvent(EVENT_TYPE.AGENCY_SPECIFIC_SERVICE_IS_SHOWN, {
                agency: {
                    show_more_exist: showMoreExist,
                    services: [item.serviceName],
                    service_position: index + 1,
                },
            });
            setViewed(true);
        }
    };

    const handleSeeMoreClick = (event) => {
        onSeeMoreClick();
        bigQuery.clickEvent(EVENT_TYPE.CLICKED_ON_SEE_MORE_IN_SERVICES, {
            agency: {
                services: [item.serviceName],
                service_position: index + 1,
            },
            page: {
                element: {
                    type: 'link',
                    text: translate('agency_page_perseus.see_more'),
                },
            },
        });
        mixpanel.event('Services - Read more clicked');
        event.stopPropagation();
    };

    useObserver({
        observableRef: componentRef,
        intersectionThreshold: 0.5,
        onObserve,
    });

    return (
        <Container
            key={item.serviceId}
            ref={componentRef}
            width={{ sm: '351px' }}
            height="332px"
            padding="8"
            margin="2"
            marginBottom="2.5"
            borderWidth="sm"
            borderColor="grey_500"
            borderRadius="lg"
            boxShadow={{
                default: 'z1',
                hover: 'z3',
            }}
            cursor="pointer"
            onClick={handleSeeMoreClick}
        >
            <PreviewItemIcon itemIndex={index} itemServiceId={item.serviceId} />
            <Heading as="h5" marginBottom="4" whiteSpace="nowrap" textOverflow="ellipsis" overflow="hidden">
                {item.serviceName}
            </Heading>
            <Stack height="96px" gap="3" direction="column" marginBottom="5" overflowY="hidden">
                {item.subServices.slice(0, 3).map((subService) => (
                    <PreviewItemSubService
                        key={subService.id}
                        name={subService.name}
                        className={atoms({
                            whiteSpace: 'nowrap',
                        })}
                    />
                ))}
            </Stack>

            <button className={moreButtonClass} onClick={handleSeeMoreClick}>
                <Text size="b_md" color="bodyPrimary">
                    <I18n k="agency_page_perseus.see_more" />
                </Text>
            </button>
        </Container>
    );
};
